import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import "./AgencyAgentEmployeeDetails.scss";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import BackArrowIcon from "../../../../components/icons/BackArrowIcon";
import { useEffect, useRef, useState } from "react";
import { AgencyEmployeeApi } from "../../../../utils/api/AgencyEmployeeApi";
import { getCdnImageUrl, getImageUrl } from "../../../../utils/helpers/common";
import toast from "react-hot-toast";
import AgencyEmployeeStatusMenu from "./AgencyAgentEmployeeStatusMenu";
import { clientRoutes } from "../../../../utils/routes";
import { AgentEmployeeApi } from "../../../../utils/api/AgentEmployeeApi";
import AgentEmployeeInfoView from "./AgencyAgentEmployeeInfoView";
import AgentEmployeeTabsView from "./AgentEmployeeTabsView";
import { CircularProgress, Modal } from "@mui/material";
import Loading from "../../../loading/Loading";
import AgencyAgentEmployeeStatusMenu from "./AgencyAgentEmployeeStatusMenu";
import AgencyAgentEmployeeTabsView from "./AgentEmployeeTabsView";
import CloseIcon from "../../../../components/icons/CloseIcon";

const getEmployeeProfileImageUrl = (uploadRecord: any) => {
  if (!uploadRecord) return "";
  if (uploadRecord.medium_image_url)
    return getCdnImageUrl(uploadRecord.medium_image_url);
  if (uploadRecord.small_image_url)
    return getCdnImageUrl(uploadRecord.small_image_url);
  if (uploadRecord.storage_key) return getCdnImageUrl(uploadRecord.storage_key);
  if (uploadRecord.compressed_file_path)
    return getImageUrl(uploadRecord.compressed_file_path);
  if (uploadRecord.original_file_path)
    return getImageUrl(uploadRecord.original_file_path);
  return "";
};

const getEmployeeLargeImageUrl = (uploadRecord: any) => {
  if (!uploadRecord) return "";
  if (uploadRecord.large_image_url)
    return getCdnImageUrl(uploadRecord.large_image_url);
  if (uploadRecord.medium_image_url)
    return getCdnImageUrl(uploadRecord.medium_image_url);
  if (uploadRecord.storage_key) return getCdnImageUrl(uploadRecord.storage_key);
  if (uploadRecord.original_file_path)
    return getImageUrl(uploadRecord.original_file_path);
  return "";
};

const AgencyAgentEmployeeDetails = () => {
  const navigate = useNavigate();
  const params: any = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") || "info";

  const [state, setState] = useState({
    isLoading: true,
    employee: null as any,
  });

  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const imageRef = useRef<HTMLImageElement>(null);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const employeeId = params?.id;

  const fetchEmployee = async () => {
    setState((p) => ({ ...p, isLoading: true }));

    const res = await AgencyEmployeeApi.getAgentEmployeeById(employeeId);

    if (res.success && res.data?.success) {
      setState({ isLoading: false, employee: res.data.data });
    } else {
      setState({ isLoading: false, employee: null });
    }
  };

  useEffect(() => {
    if (!employeeId) return;
    fetchEmployee();
  }, [employeeId]);

  useEffect(() => {
    setImageLoading(true);
    setImageError(false);
    setShowSkeleton(true);

    const employee = state.employee;
    const url = getEmployeeProfileImageUrl(employee?.profile_photo);

    if (url) {
      const img = new Image();
      img.src = url;
      if (img.complete) {
        setImageLoading(false);
        setShowSkeleton(false);
      }
    } else {
      setImageLoading(false);
      setShowSkeleton(false);
    }
  }, [state.employee?.profile_photo]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
    setTimeout(() => setShowSkeleton(false), 100);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
    setShowSkeleton(false);
  };

  const getHeaderName = () => {
    const e = state.employee;
    return (
      e?.full_name || `${e?.first_name || ""} ${e?.last_name || ""}`.trim()
    );
  };

  const getHeaderEmail = () => state.employee?.email || "";

  const getHeaderImageSrc = () =>
    getEmployeeProfileImageUrl(state.employee?.profile_photo);

  const getPreviewImageSrc = () =>
    getEmployeeLargeImageUrl(state.employee?.profile_photo) ||
    getHeaderImageSrc();

  const updateEmployeeStatus = async (nextStatus: string) => {
    if (!state.employee?._id) return;

    const res = await AgentEmployeeApi.update(state.employee._id, {
      status: nextStatus,
    });

    if (res.success && res.data?.success) {
      toast.success("Status updated");
      fetchEmployee();
    } else {
      toast.error(res?.error || res?.data?.message || "Failed to update");
    }
  };

  const renderProfileImage = () => {
    const src = getHeaderImageSrc();

    if (imageError || !src) {
      return (
        <div className="default-avatar">
          {(getHeaderName() || "A")[0]?.toUpperCase()}
        </div>
      );
    }

    return (
      <>
        {showSkeleton && <div className="image-skeleton" />}
        <img
          ref={imageRef}
          src={src}
          alt={getHeaderName() || "Employee"}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            opacity: imageLoading ? 0 : 1,
            transition: "opacity 0.3s ease-in-out",
          }}
        />
      </>
    );
  };

  const getDetails = () => {
    switch (type) {
      case "info":
        return (
          <AgentEmployeeInfoView
            employee={state.employee}
            onRefresh={fetchEmployee}
          />
        );
      case "permissions":
        return <div className="coming-soon">Permissions (coming soon)</div>;
      case "logs":
        return <div className="coming-soon">Logs (coming soon)</div>;
      default:
        return null;
    }
  };

  const canOpenPreview = !imageError && !!getHeaderImageSrc();

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="agency-employee-details-container">
          <div
            className="propertie-view-back-btn"
            onClick={() => navigate(clientRoutes.agencyAgentEmployeeList)}
          >
            <div className="back-icon">
              <BackArrowIcon />
            </div>
            <p className="bakc-text-contain">Back to list</p>
          </div>

          <div className="details-heading">
            <p>Agent employee details</p>
          </div>

          {state.isLoading ? (
            <div className="details-loader">
              <Loading />
            </div>
          ) : !state.employee ? (
            <div className="details-not-found">Employee not found</div>
          ) : (
            <>
              <div className="details-heading-name">
                <div
                  className="details-profile"
                  onClick={() => {
                    if (canOpenPreview) setIsPreviewOpen(true);
                  }}
                  style={{
                    cursor: canOpenPreview ? "pointer" : "default",
                  }}
                >
                  {renderProfileImage()}
                </div>
                <div className="details-info">
                  <p className="name">{getHeaderName()}</p>
                  <p className="email">{getHeaderEmail()}</p>
                </div>
              </div>

              <AgencyAgentEmployeeTabsView />
              {getDetails()}
            </>
          )}
        </div>

        <Modal open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)}>
          <div className="employee-image-preview-modal">
            <button
              className="employee-image-preview-close"
              onClick={() => setIsPreviewOpen(false)}
            >
              <CloseIcon />
            </button>

            <img
              className="employee-image-preview-img"
              src={getPreviewImageSrc()}
              alt="preview"
              draggable={false}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src =
                  "/images/profile_placeholder.png";
              }}
            />
          </div>
        </Modal>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default AgencyAgentEmployeeDetails;
